import CanvasPluginProvider from '../CanvasPluginProvider'
import { useIncidentStore } from '@/store/incident'
import Konva from 'konva'
import { CanvasPluginSettings } from '@/types/Canvas'
import { DeadAnimalIncident } from '@/types/Incident'
import { Reactive } from 'vue'

type ConfigType = {
  enabled: boolean
  opacity: number
  bucketX: number
  bucketY: number
}

type Buckets = Record<string, number[][]>

class DeadAnimalIncidentClusterPlugin extends CanvasPluginProvider<ConfigType> {
  reactiveProperties: Reactive<{ enabled: boolean; loading: boolean; incidents: DeadAnimalIncident[] }>

  constructor(settings: CanvasPluginSettings, config: ConfigType) {
    super(settings, 'deadAnimalClusters', config, true, true)
    this.reactiveProperties = { loading: false, enabled: settings.enabled, incidents: [] }
  }

  fetchInformation(): [Promise<void>, AbortController] {
    this.reactiveProperties.incidents = []
    this.draw()
    const controller = new AbortController()
    const promise: Promise<void> = new Promise((resolve, reject) => {
      useIncidentStore()
        .loadIncidentsCreatedBeforeSpecificTimestamp(
          'deadAnimal',
          this.settings.timestamp.toMillis(),
          [
            { key: 'messagePayload.falsePositive', operator: 'exists', value: false },
            { key: 'messagePayload.lastDetection.regions.stallbereich_lr', operator: 'exists', value: true },
          ],
          {
            only: [
              'identifier.cameraId',
              'identifier.bucketX',
              'identifier.bucketY',
              'messagePayload.lastDetection.x',
              'messagePayload.lastDetection.y',
            ],
          }
        )
        .then(incidents => {
          this.reactiveProperties.incidents = incidents
          resolve()
        })
        .catch(error => {
          reject(error)
        })
    })
    return [promise, controller]
  }

  draw() {
    this.group.destroyChildren()
    if (!this.reactiveProperties.incidents?.length) return

    const buckets: Buckets = {}

    Object.keys(this.settings.elementPositions).forEach(cameraId => {
      buckets[cameraId] = Array.from({ length: this.config.bucketY }, () => new Array(this.config.bucketX).fill(0))
    })

    let maximum = 0
    for (const incident of this.reactiveProperties.incidents as DeadAnimalIncident[]) {
      const cameraId = incident.identifier.cameraId
      if (!buckets[cameraId]) continue
      const bucketX = Math.floor(incident.messagePayload.lastDetection.x * this.config.bucketX)
      const bucketY = Math.floor(incident.messagePayload.lastDetection.y * this.config.bucketY)
      buckets[cameraId][bucketY][bucketX] += 1
      if (buckets[cameraId][bucketY][bucketX] > maximum) {
        maximum = buckets[cameraId][bucketY][bucketX]
      }
    }

    if (maximum === 0) return
    const logMax = Math.log1p(maximum)
    for (const cameraId of Object.keys(buckets)) {
      const elementPosition = this.settings.elementPositions[cameraId]
      const transform = new Konva.Transform()
      transform.translate(elementPosition.x, this.settings.elementPositions[cameraId].y)
      transform.rotate(elementPosition.rotation * (Math.PI / 180))

      const cellW = elementPosition.width / this.config.bucketX
      const cellH = elementPosition.height / this.config.bucketY

      for (let row = 0; row < this.config.bucketY; row++) {
        for (let col = 0; col < this.config.bucketX; col++) {
          const count = buckets[cameraId][row][col]
          if (count === 0) continue
          const intensity = Math.log1p(count) / logMax
          const r = Math.floor(intensity * 255)
          const g = Math.floor((1 - intensity) * 255)
          const color = `rgb(${r}, ${g}, 0)`
          const pos = transform.point({ x: cellW * col + cellW / 2, y: cellH * row + cellH / 2 })

          const rect = new Konva.Rect({
            x: pos.x,
            y: pos.y,
            width: cellW,
            height: cellH,
            rotation: elementPosition.rotation,
            strokeWidth: 0,
            offsetX: cellW / 2,
            offsetY: cellH / 2,
            fill: color,
            opacity: this.config.opacity,
          })

          rect.on('mouseenter', ev => {
            this.settings.tooltipEnable({
              x: ev.evt.clientX + 2,
              y: ev.evt.clientY + 2,
              text: `${count} Incidents, (Max is ${maximum})`,
            })
          })

          rect.on('mousemove', ev => {
            this.settings.tooltipEnable({
              x: ev.evt.clientX + 2,
              y: ev.evt.clientY + 2,
              text: `${count} Incidents, (Max is ${maximum})`,
            })
          })

          rect.on('mouseleave', () => {
            this.settings.tooltipDisable()
          })
          this.group.add(rect)
        }
      }
    }

    this.group.cache({ pixelRatio: 50, hitCanvasPixelRatio: 200 })
  }

  doesConfigChangeNeedRerender(config: ConfigType): boolean {
    return config.opacity !== this.config.opacity
  }

  doesConfigChangeNeedReload(): boolean {
    return false
  }
}

export default DeadAnimalIncidentClusterPlugin
